// src/routes/Auction.tsx
//
// RFA Phase 4 §4.3 — the live auction room.
//
// One reactive read (getAuctionView) drives the whole page; names arrive
// server-joined and are rendered verbatim. The budgets sidebar is a single
// getAuctionBudgets subscription (no per-row queries). Countdowns are
// display-only against the server's expiresAt timestamps.
//
// Rule note: there are NO roster caps during the offseason auction — cutdowns
// before the season are where caps get enforced — so no cap warnings/blocks
// appear on the bid/match forms. Contract years are NOT declared here either
// (Phase 6f): every real award lands as a provisional 1-year contract whose
// length is assigned on /contracts before cutdown.
import { useMutation, useQuery } from 'convex/react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { AuctionCommishControls } from '../components/AuctionCommishControls';
import { Countdown, formatClock } from '../components/Countdown';

const STATUS_LABELS: Record<string, string> = {
  pre: 'Pre-auction (tagging open)',
  live: 'Live',
  awaiting_match: 'Awaiting match decision',
  paused: 'Paused',
  complete: 'Complete',
};

export default function Auction() {
  const me = useQuery(api.franchises.myFranchise);
  const current = useQuery(api.rfa.init.getCurrentAuction);
  const view = useQuery(
    api.rfa.auction.getAuctionView,
    current ? { auctionId: current._id } : 'skip',
  );
  const budgets = useQuery(
    api.rfa.budget.getAuctionBudgets,
    current ? { auctionId: current._id } : 'skip',
  );
  const myBudget = useQuery(
    api.rfa.budget.getMyAuctionBudget,
    current ? { auctionId: current._id } : 'skip',
  );
  const franchises = useQuery(api.franchises.listFranchises);

  const nominate = useMutation(api.rfa.auction.nominatePlayer);
  const placeBid = useMutation(api.rfa.auction.placeBid);
  const acceptMatch = useMutation(api.rfa.auction.acceptMatch);
  const declineMatch = useMutation(api.rfa.auction.declineMatch);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nomineeId, setNomineeId] = useState<string>('');
  const [bidAmount, setBidAmount] = useState<string>('');
  // Mock-only commissioner "act as" seat ('' = self). Lets a solo dry run
  // drive absent franchises through nominate/bid/match.
  const [actingId, setActingId] = useState<string>('');

  if (me === undefined || current === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }
  if (current === null) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <h1 className="text-2xl font-bold">RFA Auction</h1>
        <p className="text-sm text-gray-600">No auction is running.</p>
        {me.isCommissioner === true && <AuctionCommishControls />}
      </div>
    );
  }
  if (view === undefined) return <div className="p-6">Loading…</div>;

  const { auction } = view;
  const status = auction.status;
  const cp = view.currentPlayer;

  // Mock-only commissioner mode: every visibility flag, budget readout, and
  // mutation call below follows the ACTING franchise (default: self).
  const isCommishMock = auction.isMock && me.isCommissioner === true;
  const actingFranchiseId: Id<'franchises'> =
    isCommishMock && actingId !== '' ? (actingId as Id<'franchises'>) : me._id;
  const actingArg = actingFranchiseId !== me._id ? { franchiseId: actingFranchiseId } : {};

  const poolEntry = cp ? view.players.find((p) => p.playerId === cp.playerId) : undefined;
  const iAmPriorOwner = poolEntry !== undefined && poolEntry.priorFranchiseId === actingFranchiseId;
  const iAmTopBidder = cp?.topBid?.franchiseId === actingFranchiseId;
  const myTagOnCurrent =
    cp !== null &&
    view.tags.some((t) => t.franchiseId === actingFranchiseId && t.playerId === cp.playerId);
  const step = cp?.isTagged ? 2 : 1;
  // Silent block (Phase 6g): the first bid must clear the opening minimum
  // (22 on a tagged player, 1 otherwise); afterwards the normal +step floor.
  const minBid = cp?.topBid ? cp.topBid.amount + step : (cp?.openingMinimum ?? 1);
  const highBid = cp?.topBid?.amount ?? 0;
  // Display-only mirror of the server's computeMatchCost: half (rounded up)
  // when the ACTING franchise's tag is on the player, else the full high bid.
  const matchCost = myTagOnCurrent ? Math.ceil(highBid / 2) : highBid;
  // Bid-form "Available" follows the acting franchise, straight from the
  // existing getAuctionBudgets subscription (no new query).
  const actingBudget =
    actingFranchiseId === me._id
      ? myBudget
      : budgets?.find((b) => b.franchiseId === actingFranchiseId);
  const taggedPlayerIds = new Set(view.tags.map((t) => t.playerId));
  const franchiseName = new Map((franchises ?? []).map((f) => [f._id, f.name] as const));
  const resolved = view.players.filter(
    (p) => p.status === 'retainedByPrior' || p.status === 'soldToBidder',
  );
  // Phase 6g: resolved rows carry the joined resolution outcome, finalCost,
  // and winner name; a bidless close resolves 'unclaimed' — retained at 0.
  const resolvedLabel = (p: (typeof view.players)[number]) => {
    const cost = p.finalCost !== null ? ` — ${p.finalCost}` : '';
    return p.status === 'retainedByPrior'
      ? p.outcome === 'unclaimed'
        ? `— retained by ${p.priorFranchiseName} (no bids)${cost}`
        : `— retained by ${p.priorFranchiseName}${cost}`
      : `— sold to ${p.winnerFranchiseName ?? 'the winning bidder'}${cost}`;
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onNominate = async (e: FormEvent) => {
    e.preventDefault();
    if (!nomineeId) {
      setError('Pick a player to nominate.');
      return;
    }
    await run(async () => {
      await nominate({
        auctionId: current._id,
        playerId: nomineeId as Id<'players'>,
        ...actingArg,
      });
      setNomineeId('');
    });
  };

  const onBid = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(bidAmount);
    if (!bidAmount.trim() || Number.isNaN(amount)) {
      setError('Enter a bid amount.');
      return;
    }
    await run(async () => {
      await placeBid({ auctionId: current._id, amount, ...actingArg });
      setBidAmount('');
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold">RFA Auction — {auction.season}</h1>

      {auction.isMock && (
        <div className="mt-3 rounded border-2 border-amber-500 bg-amber-100 px-4 py-3 text-sm font-bold text-amber-900">
          ⚠ MOCK AUCTION — practice mode, nothing is real. Results are discarded on reset. Budgets
          start from your real balance.
        </div>
      )}

      {me.isCommissioner === true && (
        <div className="mt-3">
          <AuctionCommishControls />
        </div>
      )}

      {/* Status bar */}
      <div className="mt-3 flex flex-wrap items-center gap-4 rounded border bg-white px-3 py-2 shadow-sm">
        <span className="text-sm">
          Status: <strong>{STATUS_LABELS[status] ?? status}</strong>
        </span>
        {status === 'awaiting_match' && auction.matchExpiresAt !== undefined && (
          <Countdown to={auction.matchExpiresAt} label="Match window" />
        )}
        {status === 'pre' && (
          <Link className="text-sm text-blue-600 underline" to="/auction/tags">
            Set your franchise tag →
          </Link>
        )}
      </div>

      {status === 'paused' && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          <strong>Auction paused</strong>
          {auction.pausedReason ? ` — ${auction.pausedReason}` : ''}
          {auction.pausedUntil !== undefined && (
            <> (back around {new Date(auction.pausedUntil).toLocaleTimeString()})</>
          )}
          {auction.pausedAt !== undefined &&
            auction.pausedFromStatus === 'awaiting_match' &&
            auction.matchExpiresAt !== undefined && (
              <span className="ml-3 font-mono">
                match clock frozen at {formatClock(auction.matchExpiresAt - auction.pausedAt)}
              </span>
            )}
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Nomination strip */}
      {view.nominationOrder.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {view.nominationOrder.map((entry, i) => (
            <span
              key={`${entry.franchiseId}-${i}`}
              className={`rounded-full border px-3 py-1 text-xs ${
                i === auction.nominationCursor
                  ? 'border-blue-600 bg-blue-600 font-semibold text-white'
                  : 'bg-white text-gray-700'
              }`}
            >
              {i + 1}. {entry.name}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          {status === 'complete' ? (
            <section className="rounded border bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Auction complete</h2>
              <p className="mt-1 text-sm text-gray-600">
                All players resolved. Budgets have been reset to 75 bones.
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {resolved.map((p) => (
                  <li key={p.rfaPlayerId}>
                    <span className="font-medium">{p.name}</span>{' '}
                    <span className="text-gray-500">
                      ({p.position} {p.nflTeam})
                    </span>{' '}
                    {resolvedLabel(p)}
                  </li>
                ))}
              </ul>
            </section>
          ) : status === 'pre' ? (
            <section className="rounded border bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Waiting for tags to lock</h2>
              <p className="mt-1 text-sm text-gray-600">
                Owners are setting franchise tags on{' '}
                <Link className="underline" to="/auction/tags">
                  the tags page
                </Link>
                . The commissioner opens bidding by locking tags.
              </p>
            </section>
          ) : (
            <section className="rounded border bg-white p-4 shadow-sm">
              <h2 className="font-semibold">On the block</h2>

              {/* Mock-only commissioner seat switcher */}
              {isCommishMock && view.nominationOrder.length > 0 && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2">
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-amber-900">Acting as</span>
                    <select
                      className="border rounded px-2 py-1"
                      value={actingId === '' ? me._id : actingId}
                      onChange={(e) => setActingId(e.target.value)}
                    >
                      {[
                        ...new Map(view.nominationOrder.map((f) => [f.franchiseId, f])).values(),
                      ].map((f) => (
                        <option key={f.franchiseId} value={f.franchiseId}>
                          {f.name}
                          {f.franchiseId === me._id ? ' (you)' : ''}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-amber-800">
                      Mock: drive any seat — nominate, bid, match.
                    </span>
                  </label>
                </div>
              )}

              {cp ? (
                <>
                  <div className="mt-2 flex flex-wrap items-baseline gap-2">
                    <span className="text-xl font-bold">{cp.name}</span>
                    <span className="text-sm text-gray-500">
                      {cp.position} {cp.nflTeam}
                    </span>
                    {cp.isTagged && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
                        TAGGED
                      </span>
                    )}
                    {poolEntry && (
                      <span className="text-sm text-gray-500">
                        prior owner: {poolEntry.priorFranchiseName}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 text-sm">
                    {cp.topBid ? (
                      <>
                        High bid: <strong>{cp.topBid.amount} bones</strong> —{' '}
                        {cp.topBid.franchiseName}
                        {iAmTopBidder && <span className="ml-1 text-xs text-blue-600">(you)</span>}
                      </>
                    ) : (
                      <span className="text-gray-500">
                        No bids yet — opening bid is at least{' '}
                        <strong>{cp.openingMinimum} bones</strong>
                        {cp.isTagged ? ' (tagged: even amounts only)' : ''}.
                      </span>
                    )}
                  </div>

                  {/* Soft bid clock: counts down from the LAST bid; resets on
                      every new bid (recentBids[0] is newest). Advisory only —
                      the commissioner still closes bidding manually. */}
                  {status === 'live' && cp.recentBids.length > 0 && (
                    <div className="mt-1">
                      <Countdown
                        to={cp.recentBids[0].ts + auction.bidClockSeconds * 1000}
                        label="Bid clock"
                        expiredText={
                          me.isCommissioner
                            ? 'expired — you can close bidding'
                            : 'expired — commissioner may close bidding'
                        }
                      />
                    </div>
                  )}

                  {/* Bid form */}
                  {status === 'live' && (
                    <form onSubmit={onBid} className="mt-3 flex flex-wrap items-end gap-3">
                      <label className="flex flex-col text-sm">
                        <span className="text-gray-600 mb-1">
                          {!cp.topBid && cp.isTagged
                            ? `Bid (opening bid ≥ ${cp.openingMinimum}, even amounts)`
                            : `Bid (min ${minBid}${cp.isTagged ? ', even amounts only' : ''})`}
                        </span>
                        <input
                          type="number"
                          min={minBid}
                          step={step}
                          placeholder={String(minBid)}
                          className="border rounded px-2 py-1 w-28"
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e.target.value)}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-3 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                      >
                        {submitting ? 'Bidding…' : 'Place bid'}
                      </button>
                      <span className="text-sm text-gray-600">
                        Available: <strong>{actingBudget?.available ?? '—'}</strong> bones
                      </span>
                    </form>
                  )}

                  {/* Match banner — prior owner only */}
                  {status === 'awaiting_match' && iAmPriorOwner && cp.topBid && (
                    <div className="mt-3 rounded border border-blue-300 bg-blue-50 p-3">
                      <div className="text-sm font-semibold">
                        Your match decision: retain {cp.name} for {matchCost} bones
                        {myTagOnCurrent && (
                          <span className="ml-1 font-normal text-gray-600">
                            (your tag — half of {highBid}, rounded up)
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap items-end gap-3">
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() =>
                            run(() =>
                              acceptMatch({
                                auctionId: current._id,
                                playerId: cp.playerId,
                                ...actingArg,
                              }),
                            )
                          }
                          className="px-3 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                        >
                          Accept match
                        </button>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() =>
                            run(() =>
                              declineMatch({
                                auctionId: current._id,
                                playerId: cp.playerId,
                                ...actingArg,
                              }),
                            )
                          }
                          className="px-3 py-2 border border-red-300 text-red-700 rounded text-sm disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-600">
                        Contract years are assigned before cutdown — see{' '}
                        <Link className="underline" to="/contracts">
                          Contracts
                        </Link>
                        .
                      </p>
                    </div>
                  )}
                  {status === 'awaiting_match' && !iAmPriorOwner && (
                    <p className="mt-3 text-sm text-gray-600">
                      Waiting for {poolEntry?.priorFranchiseName ?? 'the prior owner'} to match or
                      decline…
                    </p>
                  )}

                  {/* Bid history */}
                  {cp.recentBids.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-gray-600">Bid history</h3>
                      <ul className="mt-1 space-y-0.5 text-sm">
                        {cp.recentBids.map((b) => (
                          <li key={`${b.franchiseId}-${b.ts}-${b.amount}`}>
                            <span className="font-mono">{b.amount}</span> — {b.franchiseName}{' '}
                            <span className="text-xs text-gray-400">
                              {new Date(b.ts).toLocaleTimeString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : status === 'live' ? (
                view.currentNominator?.franchiseId === actingFranchiseId ? (
                  <form onSubmit={onNominate} className="mt-2 flex flex-wrap items-end gap-3">
                    <label className="flex flex-col text-sm">
                      <span className="text-gray-600 mb-1">Your turn — nominate a player</span>
                      <select
                        className="border rounded px-2 py-1 min-w-64"
                        value={nomineeId}
                        onChange={(e) => setNomineeId(e.target.value)}
                      >
                        <option value="">Select a player…</option>
                        {view.players
                          .filter((p) => p.status === 'available')
                          .map((p) => (
                            <option key={p.rfaPlayerId} value={p.playerId}>
                              {p.name} ({p.position} {p.nflTeam}) — {p.priorFranchiseName}
                              {taggedPlayerIds.has(p.playerId) ? ' [TAGGED]' : ''}
                            </option>
                          ))}
                      </select>
                    </label>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-3 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      {submitting ? 'Nominating…' : 'Nominate'}
                    </button>
                  </form>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Waiting for {view.currentNominator?.name ?? 'the next franchise'} to nominate…
                  </p>
                )
              ) : (
                <p className="mt-2 text-sm text-gray-600">No player on the block.</p>
              )}
            </section>
          )}

          {/* Resolved-so-far list while the auction is running */}
          {status !== 'complete' && resolved.length > 0 && (
            <section className="rounded border bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Resolved</h2>
              <ul className="mt-2 space-y-1 text-sm">
                {resolved.map((p) => (
                  <li key={p.rfaPlayerId}>
                    <span className="font-medium">{p.name}</span>{' '}
                    <span className="text-gray-500">
                      ({p.position} {p.nflTeam})
                    </span>{' '}
                    {resolvedLabel(p)}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <section className="rounded border bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Budgets</h2>
            <p className="text-xs text-gray-500">available / balance</p>
            {budgets === undefined ? (
              <p className="mt-2 text-sm text-gray-500">Loading…</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {[...budgets]
                  .sort((a, b) =>
                    (franchiseName.get(a.franchiseId) ?? '').localeCompare(
                      franchiseName.get(b.franchiseId) ?? '',
                    ),
                  )
                  .map((b) => (
                    <li
                      key={b.franchiseId}
                      className={`flex justify-between ${
                        b.franchiseId === me._id ? 'font-semibold' : ''
                      }`}
                    >
                      <span>
                        {franchiseName.get(b.franchiseId) ?? '…'}
                        {b.franchiseId === me._id && (
                          <span className="ml-1 text-xs text-blue-600">(you)</span>
                        )}
                      </span>
                      <span className={`font-mono ${b.available < 0 ? 'text-red-600' : ''}`}>
                        {b.available} / {b.balance}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </section>

          <section className="rounded border bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Franchise tags</h2>
            {view.tags.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No tags.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {view.tags.map((t) => (
                  <li key={`${t.franchiseId}-${t.playerId}`}>
                    <span className="font-medium">{t.franchiseName}</span> → {t.playerName}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
